// LE FAMILIER (Lot C T6, refonte « beaucoup mieux » 21/07 soir, puis « compagnon VIVANT » 21/07 nuit) :
// compagnon cosmétique qui SUIT le coureur. 100 % COSMÉTIQUE et hors simulation : rien ici n'entre
// dans le hash d'état ni le rejeu réseau (lecture seule : positions écran, événements, capteurs).
// Quatre étages :
//   1. TRACE : le pet rejoue la trajectoire du coureur avec un retard EN TICKS (ring buffer +
//      interpolation), lissé -> il trotte DERRIÈRE, jamais collé.
//   2. GARDE-DISTANCE : sous PET_STOP_GAP il passe en OISIVETÉ et se FIGE à distance polie ;
//      il ne repart que quand le coureur s'éloigne (PET_RESUME_GAP).
//   3. VIE : poses d'oisiveté + ACTIONS SPÉCIALES planifiées (hurlement, tête pivotée, boule...)
//      avec effets dessinés et sons doux (audio.petCue).
//   4. RÉACTIONS (21/07 nuit) : le pet réagit au JEU — joie à l'or ramassé, gratte quand tu creuses,
//      bondit au trampoline, ALERTE quand un moine approche (grognement du loup), ACCOURT et VEILLE
//      ton corps à ta mort, CÂLIN automatique quand tu restes près de lui. Plus l'ancrage physique :
//      ombre portée, petit saut aux marches, poussière de course, traînée du follet, et l'araignée
//      qui marche AU PLAFOND quand il y en a un.
import { petCue } from './audio'
import type { RenderCtx } from './render'
import { PET_HD_ROWS, PET_SPRITES, petsSheet } from './sprites'
import { FLYING_PETS, PET_SCALE, posePet } from './pet-allure'
import { blitPet } from './pet-papier'

/** Les compagnons dont la silhouette papier existe (cf. client/pet-papier.ts). */
// ⚠️ Un compagnon ABSENT de ce Set retombe sur la feuille pets.png (PET_HD_ROWS), où les trois
// derniers n'ont aucune ligne : il ne se dessinerait donc pas du tout. Ajouter une silhouette dans
// pet-papier.ts sans l'inscrire ici, c'est livrer un familier invisible — un test le refuse.
export const PETS_PAPIER = new Set(['pet-chat', 'pet-canari', 'pet-perroquet', 'pet-araignee',
  'pet-serpent', 'pet-loup', 'pet-chouette', 'pet-renard', 'pet-dragonnet', 'pet-follet',
  'pet-taupe', 'pet-chauve-souris', 'pet-crapaud',
  // LES TROIS ÉLUS DE LA VAGUE 2 (13/08) : leurs silhouettes existaient déjà dans pet-papier,
  // dessinées le 05/08 — il ne leur manquait que d'être au catalogue et autorisées ici.
  'pet-herisson', 'pet-axolotl', 'pet-cameleon'])

/**
 * PEUT-ON PEINDRE CE COMPAGNON ? La seule autorité, et la seule garde que les écrans ont le droit
 * d'utiliser avant d'appeler `updateAndDrawPet`.
 *
 * 🚨 CE QU'ELLE RÉPARE (11/08). `render.ts` et `mine-render.ts` filtraient sur `PET_SPRITES`, la
 * vieille feuille 16 px restée à DIX compagnons. La taupe, la chauve-souris et le crapaud
 * n'y ont pas de cellule : le rendu sortait avant même d'atteindre `blitPet`, qui sait pourtant
 * les peindre. Trois familiers gagnables, équipables, et strictement invisibles en jeu pendant
 * douze jours — sans une ligne d'erreur nulle part.
 *
 * La question à poser n'est pas « ce pet est-il sur la feuille ? » mais « existe-t-il UN chemin
 * pour le dessiner ? » — et `drawPetAt` en connaît trois, dans cet ordre : la silhouette papier,
 * la feuille HD, la feuille 16 px. Cette fonction énumère exactement ces trois-là, donc elle ne
 * peut pas se désynchroniser du rendu sans qu'on le voie.
 */
export function petDessinable(petId: string | null | undefined): petId is string {
  if (!petId) return false
  return PETS_PAPIER.has(petId) || PET_HD_ROWS[petId] !== undefined || PET_SPRITES[petId] !== undefined
}

const TS = 48         // une case terrain à l'écran (16 px * SCALE 3) — cf. render.ts
const LAG_TICKS = 14  // retard de la trace, EN TICKS (~0,47 s à 30 Hz) -> le pet suit ~1,75 case derrière
const CAP = 96        // ring buffer (~3,2 s à 30 Hz)

export interface PetSample { t: number; x: number; y: number }

const histories = new Map<number, PetSample[]>()

// État de COMPORTEMENT par coureur (étages 2-4). Cosmétique pur, remis à zéro avec les traces.
interface PetBehavior {
  mode: 'follow' | 'idle'
  x: number; y: number; face: number
  idleAt: number       // début de l'oisiveté
  nextSpecial: number  // prochaine action spéciale
  specialAt: number    // début de l'action spéciale en cours (-1 = aucune)
  seeded: boolean
  // --- réactions (étage 4) ---
  reactKind: PetReaction | 'cuddle' | null
  reactAt: number
  hopUntil: number     // petit saut (marche / trampoline) : fin du saut en temps logique
  lastEventTick: number
  nearSince: number    // depuis quand le coureur est immobile tout près (déclenche le câlin)
  cuddleAt: number     // dernier câlin (espacement)
  alertCueAt: number   // dernier grognement d'alerte (espacement)
  ceiling: boolean     // araignée : accrochée au plafond
  ceilCount: number    // hystérésis plafond (frames de confirmation)
}
const behaviors = new Map<number, PetBehavior>()

/** Vide traces ET comportements (nouveau niveau / partie / changement solo↔multi). */
export function resetPetHistory(): void {
  histories.clear()
  behaviors.clear()
}

/** Ajoute un échantillon à la trace du coureur `idx`. AUTO-RESET sur discontinuité. */
export function pushPetHistory(idx: number, t: number, x: number, y: number): void {
  let history = histories.get(idx)
  if (!history) { history = []; histories.set(idx, history) }
  const last = history[history.length - 1]
  if (last) {
    const back = t < last.t
    const gap = t - last.t > LAG_TICKS
    const jump = Math.abs(x - last.x) > 4 * TS || Math.abs(y - last.y) > 4 * TS
    if (back || gap || jump) {
      history.length = 0
      behaviors.delete(idx)
    } else if (t <= last.t) {
      last.x = x; last.y = y
      return
    }
  }
  history.push({ t, x, y })
  if (history.length > CAP) history.shift()
}

/** Échantillon interpolé à `targetT` — PUR. */
export function petSampleAt(samples: readonly PetSample[], targetT: number): { x: number; y: number } | null {
  const n = samples.length
  if (n === 0) return null
  const first = samples[0]!
  if (targetT <= first.t) return { x: first.x, y: first.y }
  const last = samples[n - 1]!
  if (targetT >= last.t) return { x: last.x, y: last.y }
  for (let i = 1; i < n; i++) {
    const b = samples[i]!
    if (b.t >= targetT) {
      const a = samples[i - 1]!
      const span = b.t - a.t
      const f = span > 0 ? (targetT - a.t) / span : 0
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f }
    }
  }
  return { x: last.x, y: last.y }
}

/** Position écran de la TRACE du coureur `idx` il y a LAG_TICKS ticks (null si trace vide). */
export function petLaggedPos(idx: number, t: number): { x: number; y: number } | null {
  const history = histories.get(idx)
  if (!history) return null
  return petSampleAt(history, t - LAG_TICKS)
}

/** Flottement vertical (px) des volants : sinus doux — PUR. */
export function petFloatOffset(t: number): number {
  return Math.sin(t * 0.15) * 5
}

/** Frame de trot (0|1) liée à la POSITION x — fige à l'arrêt. PUR. */
export function petCatFrame(x: number): 0 | 1 {
  return (Math.floor(x / 8) & 1) as 0 | 1
}

/** Frame de vol (0|1) liée au TICK — bat en continu. PUR. */
export function petCanariFrame(t: number): 0 | 1 {
  return (Math.floor(t / 2) & 1) as 0 | 1
}

/**
 * Avancement du cycle de pas (0 à 1), lié à la DISTANCE parcourue et non au temps : une bête qui
 * avance doucement pose ses pattes doucement, et une bête arrêtée ne pédale pas dans le vide.
 * Même période que `petCatFrame` (16 px, deux appuis), en continu au lieu de deux images. PUR.
 */
export function petPhasePas(x: number): number {
  const p = (x / 16) % 1
  return p < 0 ? p + 1 : p
}

// ---------------------------------------------------------------- comportement (étage 2)
export const PET_STOP_GAP = 1.35 * TS
export const PET_RESUME_GAP = 2.1 * TS
/** Transition de mode — PURE (testable) : hystérésis arrêt/reprise autour du coureur. */
export function petModeStep(mode: 'follow' | 'idle', dist: number): 'follow' | 'idle' {
  return mode === 'follow' ? (dist < PET_STOP_GAP ? 'idle' : 'follow') : (dist > PET_RESUME_GAP ? 'follow' : 'idle')
}

// ---------------------------------------------------------------- réactions (étage 4)
export type PetReaction = 'joy' | 'dig' | 'hop'
/** Réaction déclenchée par les événements du tick — PURE (testable). `cellNear` dit si une case est
 *  proche du coureur (l'événement `gold`/`dig` ne porte pas l'index du joueur, on infère par la
 *  distance : celui qui ramasse est SUR la case). Priorité : joie > saut > grattage. */
export function petReactionFor(
  events: ReadonlyArray<{ type: string; runner?: number; by?: string; cell?: number }>,
  idx: number,
  cellNear: (cell: number) => boolean,
): PetReaction | null {
  let out: PetReaction | null = null
  for (const e of events) {
    if (e.type === 'gold' && e.by === 'runner' && e.cell !== undefined && cellNear(e.cell)) return 'joy'
    if (e.type === 'bounce' && e.runner === idx) out = out ?? 'hop'
    if (e.type === 'dig' && e.cell !== undefined && cellNear(e.cell)) out = out ?? 'dig'
  }
  return out
}
const REACT_DUR: Record<string, number> = { joy: 26, dig: 24, cuddle: 55 }

/** Capteurs fournis par render() (lecture seule de l'état — jamais d'écriture). */
export interface PetSensors {
  alive: boolean
  events: ReadonlyArray<{ type: string; runner?: number; by?: string; cell?: number }>
  goldDx: number | null                     // chouette : direction de l'or le plus proche
  monkDx: number | null                     // moine ACTIF à < 4 cases : direction (sinon null)
  runnerMoving: boolean                     // le coureur a bougé depuis le tick précédent
  cellNear: (cell: number) => boolean       // la case est-elle à < ~1,3 case du coureur ?
  solidAt: (wx: number, wy: number) => boolean // terrain solide en px écran (plafond de l'araignée)
}

// FLYING_PETS et PET_SCALE vivent dans pet-allure.ts (module pur) : la planche /pages/pets.html
// les lisait en les RECOPIANT, et sa copie était déjà périmée. Ré-exportés ici, où le jeu les cherche.
export { FLYING_PETS, PET_SCALE }
const SPECIAL_DUR = 66
const hash32 = (n: number): number => { let h = (n * 2654435761) >>> 0; h ^= h >>> 13; return (h * 2246822519) >>> 0 }
const nextSpecialAt = (t: number, idx: number): number => t + 300 + hash32(Math.floor(t) * 31 + idx) % 330

/** Met à jour ET dessine le familier du coureur `idx`. Appelé 1×/frame par render() (hors sim). */
export function updateAndDrawPet(
  rc: RenderCtx, idx: number, petId: string,
  runnerX: number, runnerY: number, runnerFace: number,
  tick: number, alpha: number, sensors: PetSensors,
): void {
  const t = tick + alpha
  if (sensors.alive) pushPetHistory(idx, t, runnerX, runnerY)
  const target = petLaggedPos(idx, t)
  if (!target) return
  let st = behaviors.get(idx)
  if (!st) {
    st = {
      mode: 'follow', x: target.x, y: target.y, face: runnerFace, idleAt: 0, nextSpecial: 0,
      specialAt: -1, seeded: true, reactKind: null, reactAt: -1, hopUntil: -1, lastEventTick: -1,
      nearSince: -1, cuddleAt: -9999, alertCueAt: -9999, ceiling: false, ceilCount: 0,
    }
    behaviors.set(idx, st)
  }

  // ---- MORT DU COUREUR : le compagnon accourt et veille le corps (prioritaire sur tout) ----
  if (!sensors.alive) {
    const side = st.x < runnerX ? -1 : 1
    const gx = runnerX + side * TS * 0.75 // se poste JUSTE à côté du corps, pas dessus
    st.x += (gx - st.x) * 0.08
    st.y += (runnerY - st.y) * 0.08
    st.face = st.x < runnerX ? 1 : -1 // tourné vers le coureur
    st.specialAt = -1; st.reactKind = null; st.nearSince = -1
    drawPetAt(rc, idx, st, petId, t, true, false)
    return
  }

  // ---- ÉVÉNEMENTS DU TICK (une seule fois par tick) : joie / saut / grattage ----
  if (tick !== st.lastEventTick) {
    st.lastEventTick = tick
    const r = petReactionFor(sensors.events, idx, sensors.cellNear)
    if (r === 'hop') st.hopUntil = t + 9
    else if (r && st.reactKind === null) {
      st.reactKind = r; st.reactAt = t
      if (r === 'joy') { st.hopUntil = t + 8; petCue(petId, 'joy') }
    }
  }
  if (st.reactKind && st.reactKind !== 'cuddle' && t - st.reactAt > (REACT_DUR[st.reactKind] ?? 24)) st.reactKind = null
  if (st.reactKind === 'cuddle' && t - st.reactAt > REACT_DUR.cuddle!) st.reactKind = null

  // ---- suivi / oisiveté (étage 2) ----
  const dist = Math.hypot(runnerX - st.x, runnerY - st.y)
  const prevMode = st.mode
  st.mode = petModeStep(st.mode, dist)

  let specialJustStarted = false
  if (st.mode === 'follow') {
    const k = st.seeded ? 1 : 0.22
    const py = st.y
    st.x += (target.x - st.x) * k
    st.y += (target.y - st.y) * k
    st.seeded = false
    const dx = target.x - st.x
    if (Math.abs(dx) > 0.6) st.face = dx < 0 ? -1 : 1
    st.specialAt = -1; st.nearSince = -1
    // petit saut d'ancrage : la trace monte/descend d'une marche -> arc bref (terrestres)
    if (!FLYING_PETS.has(petId) && st.hopUntil < t && Math.abs(target.y - py) > 3.2) st.hopUntil = t + 8
  } else {
    if (prevMode === 'follow') { st.idleAt = t; st.nextSpecial = t + 90 + hash32(idx * 7 + Math.floor(t)) % 150 }
    const watchDx = sensors.monkDx !== null ? sensors.monkDx
      : petId === 'pet-chouette' && sensors.goldDx !== null ? sensors.goldDx
        : runnerX - st.x
    if (Math.abs(watchDx) > 4) st.face = watchDx < 0 ? -1 : 1
    if (st.specialAt >= 0 && t - st.specialAt > SPECIAL_DUR) st.specialAt = -1
    if (st.specialAt < 0 && st.reactKind === null && sensors.monkDx === null && t >= st.nextSpecial) {
      st.specialAt = t
      st.nextSpecial = nextSpecialAt(t, idx)
      specialJustStarted = true
    }
    // ---- CÂLIN : le coureur reste immobile tout près -> le pet vient se frotter (cœurs) ----
    if (!sensors.runnerMoving && dist < 0.95 * TS) {
      if (st.nearSince < 0) st.nearSince = t
      if (t - st.nearSince > 45 && t - st.cuddleAt > 360 && st.reactKind === null && st.specialAt < 0) {
        st.reactKind = 'cuddle'; st.reactAt = t; st.cuddleAt = t
        petCue(petId, 'cuddle')
      }
    } else st.nearSince = -1
  }
  if (specialJustStarted) petCue(petId, 'special')

  // ---- ALERTE moine : grognement espacé (loup et chat seulement, les plus « gardiens ») ----
  if (sensors.monkDx !== null && (petId === 'pet-loup' || petId === 'pet-chat') && t - st.alertCueAt > 180) {
    st.alertCueAt = t
    petCue(petId, 'alert')
  }

  // ---- araignée : plafond (hystérésis pour ne pas clignoter aux transitions de terrain) ----
  if (petId === 'pet-araignee') {
    const above = sensors.solidAt(st.x + TS / 2, st.y - TS / 2)
    st.ceilCount = Math.max(0, Math.min(20, st.ceilCount + (above ? 1 : -1)))
    if (st.ceilCount >= 20) st.ceiling = true
    else if (st.ceilCount <= 0) st.ceiling = false
  }

  drawPetAt(rc, idx, st, petId, t, false, sensors.monkDx !== null)
}

// ---------------------------------------------------------------- rendu (HD + ancrage + effets)
function drawPetAt(rc: RenderCtx, idx: number, st: PetBehavior, petId: string, t: number, mourning: boolean, alert: boolean): void {
  const { ctx } = rc
  const scale = PET_SCALE[petId] ?? 0.6
  const size = TS * scale
  const flying = FLYING_PETS.has(petId)
  const special = st.specialAt >= 0
  const idle = st.mode === 'idle' || mourning
  const react = st.reactKind
  const spiderHang = special && petId === 'pet-araignee' && !st.ceiling
  const hangT = spiderHang ? Math.min(1, (t - st.specialAt) / 12) * (1 - Math.max(0, (t - st.specialAt - (SPECIAL_DUR - 12)) / 12)) : 0

  // frame : sert aux DEUX FEUILLES DE SECOURS (HD et 16 px), qui n'ont que des images figées.
  // La silhouette papier, elle, ne s'en sert plus : elle reçoit la pose continue calculée ci-dessous.
  let frame: number
  if (mourning) frame = 2
  else if (react === 'joy' || react === 'dig') frame = (Math.floor(t / 2) & 1)
  else if (react === 'cuddle') frame = 2
  else if (idle) frame = special ? 3 : 2
  else frame = flying ? petCanariFrame(t) : petCatFrame(st.x)

  // saut (marche franchie, trampoline, bond de joie) : arc bref au-dessus de l'ancrage
  const hopP = st.hopUntil > t ? 1 - (st.hopUntil - t) / 9 : -1
  const hop = hopP >= 0 ? Math.sin(Math.PI * hopP) * 7 : 0
  // ⚠️ LE « BOB » DE REPOS A ÉTÉ RETIRÉ ICI (11/08), et c'est un vrai piège évité. Il ajoutait
  // `Math.sin(t * 0.08)` à la hauteur du terrestre oisif — or la respiration de la nouvelle pose
  // bat à 0,075, presque la même période et en opposition de phase. Les deux s'ANNULAIENT : mesuré
  // au harnais, un chat à l'arrêt bougeait moins APRÈS l'ajout de la respiration qu'avant.
  // Un seul endroit décide désormais du souffle : la pose.
  const x = st.x + (TS - size) / 2
  const baseY = flying
    ? st.y + (TS - size) / 2 - TS * 0.45 + petFloatOffset(t) * (idle ? 0.5 : 1)
    : st.y + TS - size
  const ceiling = petId === 'pet-araignee' && st.ceiling && !mourning
  const yy = (ceiling ? st.y + 1 : baseY - hop) - hangT * TS * 0.8

  // ---- ombre portée (ancrage au sol) : sous le pet, à la base de SA case ----
  if (!ceiling && !spiderHang) {
    const groundY = st.y + TS - 2.5
    const liftK = flying ? 0.55 : Math.max(0.35, 1 - hop / 10)
    ctx.save()
    ctx.fillStyle = `rgba(0,0,0,${(flying ? 0.16 : 0.24) * liftK})`
    ctx.beginPath()
    ctx.ellipse(st.x + TS / 2, groundY, size * 0.34 * liftK + 2, 2.6, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // ---- poussière de course (terrestres au trot) ----
  if (!flying && !idle && !react && Math.abs(Math.sin(t * 0.9)) > 0.1) {
    const p1 = (t * 0.16) % 1, p2 = (t * 0.16 + 0.5) % 1
    ctx.save()
    for (const p of [p1, p2]) {
      ctx.globalAlpha = (1 - p) * 0.3
      ctx.fillStyle = '#b8b2c8'
      ctx.fillRect(st.x + TS / 2 - st.face * (size * 0.4 + p * 8), st.y + TS - 4 - p * 5, 2, 2)
    }
    ctx.restore()
  }
  // ---- traînée du follet (échantillons récents de la trace, additifs) ----
  if (petId === 'pet-follet' && !idle) {
    const hist = histories.get(idx)
    if (hist) {
      ctx.save(); ctx.globalCompositeOperation = 'lighter'
      for (let k = 1; k <= 3; k++) {
        const s = petSampleAt(hist, t - LAG_TICKS - k * 3)
        if (!s) break
        ctx.globalAlpha = 0.22 * (1 - k / 4)
        ctx.fillStyle = '#7ff0f5'
        ctx.beginPath(); ctx.arc(s.x + TS / 2, s.y + (TS - size) / 2 - TS * 0.45 + size / 2, size * 0.16 * (1 - k / 5), 0, Math.PI * 2); ctx.fill()
      }
      ctx.restore()
    }
  }
  // ---- halo du follet ----
  if (petId === 'pet-follet') {
    const swell = special ? 1.5 : 1
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    const r = size * (0.62 + 0.08 * Math.sin(t * 0.2)) * swell
    const g = ctx.createRadialGradient(x + size / 2, yy + size / 2, 0, x + size / 2, yy + size / 2, r)
    g.addColorStop(0, 'rgba(127,240,245,0.30)'); g.addColorStop(1, 'rgba(127,240,245,0)')
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x + size / 2, yy + size / 2, r, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  }
  if (spiderHang) {
    ctx.save(); ctx.strokeStyle = 'rgba(207,207,216,0.9)'; ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(x + size / 2, yy - TS * 1.2); ctx.lineTo(x + size / 2, yy + size * 0.2); ctx.stroke()
    ctx.restore()
  }

  // ---- sprite (HD pets.png, secours 16 px) ; araignée au plafond = retournée ----
  const mirror = st.face < 0
  const hd = petsSheet()
  const row = PET_HD_ROWS[petId]
  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.translate(mirror ? x + size : x, ceiling ? yy + size : yy)
  ctx.scale(mirror ? -1 : 1, ceiling ? -1 : 1)
  // REFONTE « papier découpé » (28/07) : le compagnon est peint à la volée, comme
  // le héros, au lieu d'être blitté depuis pets.png. La feuille reste en secours
  // si jamais une silhouette manquait.
  // ⚠️ LA FRAME EST ENFIN TRANSMISE (31/07). Elle était calculée juste au-dessus puis jetée :
  // les treize compagnons étaient des images fixes qu'on translatait, donc qui GLISSAIENT.
  // POSE CONTINUE (11/08) : la bête bascule, respire, s'étire en sautant et traîne du corps dans
  // les virages, au lieu d'alterner entre deux images. Calculée à partir de l'état réel — la phase
  // du pas suit la DISTANCE parcourue, donc une bête lente marche lentement.
  const pose = posePet({
    t,
    phase: petPhasePas(st.x),
    volant: flying,
    idle,
    special,
    // 'hop' n'atteint jamais reactKind (il part directement dans hopUntil) ; on le neutralise
    // ici pour que le type de l'allure ne connaisse que les réactions qui posent VRAIMENT.
    react: react === 'hop' ? null : react,
    mourning,
    hop: hopP,
  })
  if (PETS_PAPIER.has(petId)) blitPet(ctx, petId, size, 0, frame, pose)
  else if (hd && row !== undefined) {
    ctx.drawImage(hd, frame * 48, row * 48, 48, 48, 0, 0, size, size)
  } else {
    const frames = PET_SPRITES[petId]
    if (frames) { const sr = frames[(frame % 2) as 0 | 1]; ctx.drawImage(rc.sheet, sr.sx, sr.sy, 16, 16, 0, 0, size, size) }
  }
  ctx.restore()

  // ---- effets par-dessus ----
  if (mourning) { // veillée : une petite goutte qui perle, lentement
    const p = (t * 0.015) % 1
    ctx.save(); ctx.globalAlpha = p < 0.6 ? 0.75 : 0.75 * (1 - (p - 0.6) / 0.4)
    ctx.fillStyle = '#9fc8e8'
    ctx.fillRect(x + size * (st.face < 0 ? 0.25 : 0.75), yy + size * 0.3 + p * 6, 2, 3)
    ctx.restore()
  } else if (react === 'joy') { // étincelles dorées de joie
    ctx.save()
    for (let i = 0; i < 3; i++) {
      const p = ((t - st.reactAt) * 0.05 + i * 0.33) % 1
      ctx.globalAlpha = 1 - p
      ctx.fillStyle = i % 2 ? '#fff2b0' : '#ffd23f'
      ctx.fillRect(x + size / 2 + Math.sin(i * 2.3 + t * 0.2) * size * 0.5, yy - 3 - p * 9, 2, 2)
    }
    ctx.restore()
  } else if (react === 'dig') { // il gratte : gravats qui giclent derrière
    ctx.save()
    for (let i = 0; i < 3; i++) {
      const p = ((t - st.reactAt) * 0.06 + i * 0.3) % 1
      ctx.globalAlpha = (1 - p) * 0.8
      ctx.fillStyle = '#8a7a6a'
      ctx.fillRect(st.x + TS / 2 - st.face * (size * 0.35 + p * 10), st.y + TS - 4 - Math.sin(p * Math.PI) * 8, 2, 2)
    }
    ctx.restore()
  } else if (react === 'cuddle') { // cœurs qui montent
    ctx.save()
    for (let i = 0; i < 2; i++) {
      const p = ((t - st.reactAt) * 0.022 + i * 0.5) % 1
      ctx.globalAlpha = Math.min(1, (1 - p) * 1.4)
      ctx.fillStyle = '#ff8aa0'
      const hx = x + size / 2 + Math.sin((p * 5) + i * 3) * 4, hy = yy - 4 - p * 12
      ctx.fillRect(hx - 1.5, hy, 1.5, 1.5); ctx.fillRect(hx + 0.5, hy, 1.5, 1.5); ctx.fillRect(hx - 1, hy + 1.4, 2.6, 1.8)
    }
    ctx.restore()
  } else if (alert && st.mode === 'idle') { // « ! » d'alerte au-dessus de la tête
    const p = 0.5 + 0.5 * Math.sin(t * 0.35)
    ctx.save(); ctx.globalAlpha = 0.55 + p * 0.45
    ctx.fillStyle = '#ff6a5a'
    ctx.fillRect(x + size / 2 - 1, yy - 10, 2.4, 5); ctx.fillRect(x + size / 2 - 1, yy - 3.6, 2.4, 2.2)
    ctx.restore()
  } else if (special) drawSpecialFx(ctx, petId, x + size / 2, yy, size, t - st.specialAt, st.face)
  else if (idle && petId === 'pet-renard' && t - st.idleAt > 260) {
    const ph = (t * 0.03) % 1
    ctx.save(); ctx.fillStyle = `rgba(210,215,230,${(0.7 - ph * 0.6).toFixed(3)})`
    ctx.font = '600 9px ui-monospace, monospace'
    ctx.fillText('z', x + size * 0.8, yy - 2 - ph * 8)
    ctx.restore()
  }
}

// Petits effets des poses SPÉCIALES (frame 3) — cosmétiques, déterministes au t.
function drawSpecialFx(ctx: CanvasRenderingContext2D, petId: string, cx: number, top: number, size: number, dt: number, face: number): void {
  const dir = face < 0 ? -1 : 1
  ctx.save()
  if (petId === 'pet-loup') {
    ctx.strokeStyle = 'rgba(200,206,220,0.7)'; ctx.lineWidth = 1
    for (let i = 0; i < 3; i++) {
      const p = (dt * 0.03 + i * 0.33) % 1
      ctx.globalAlpha = 1 - p
      ctx.beginPath(); ctx.arc(cx + dir * size * 0.3, top - 2 - p * 10, 2 + p * 6, dir < 0 ? Math.PI * 0.75 : -Math.PI * 0.25, dir < 0 ? Math.PI * 1.25 : Math.PI * 0.25); ctx.stroke()
    }
  } else if (petId === 'pet-chouette') {
    const p = 0.5 + 0.5 * Math.sin(dt * 0.3)
    ctx.fillStyle = `rgba(255,210,63,${(p * 0.9).toFixed(3)})`
    ctx.fillRect(cx - 1, top - 6, 2, 2); ctx.fillRect(cx + dir * 5 - 1, top - 3, 2, 2)
  } else if (petId === 'pet-dragonnet') {
    for (let i = 0; i < 4; i++) {
      const p = (dt * 0.04 + i * 0.25) % 1
      ctx.globalAlpha = 1 - p
      ctx.fillStyle = i % 2 ? '#ffe36b' : '#ff8a3c'
      ctx.fillRect(cx + dir * (size * 0.45 + p * 10), top + size * 0.3 - p * 4 + Math.sin(p * 9 + i) * 2, 2, 2)
    }
  } else if (petId === 'pet-perroquet') {
    for (let i = 0; i < 2; i++) {
      const p = (dt * 0.02 + i * 0.5) % 1
      ctx.globalAlpha = 1 - p
      ctx.fillStyle = i ? '#2f9e4e' : '#e8483a'
      ctx.fillRect(cx - dir * (4 + i * 5) + Math.sin(p * 8) * 3, top + size * 0.3 + p * 12, 2, 2)
    }
  } else if (petId === 'pet-canari') {
    const p = (dt * 0.025) % 1
    ctx.globalAlpha = 1 - p
    ctx.fillStyle = '#ffd23f'
    const nx = cx + dir * 6, ny = top - 2 - p * 9
    ctx.fillRect(nx, ny, 1.5, 5); ctx.fillRect(nx - 3, ny + 3, 3, 2.5); ctx.fillRect(nx, ny, 4, 1.5)
  } else if (petId === 'pet-serpent') {
    if (Math.floor(dt / 4) % 2 === 0) {
      ctx.fillStyle = '#e83a55'
      ctx.fillRect(cx + dir * size * 0.5, top + size * 0.35, dir * 4, 1.2)
    }
  } else if (petId === 'pet-follet') {
    for (let i = 0; i < 3; i++) {
      const a = dt * 0.08 + i * 2.1
      ctx.fillStyle = 'rgba(234,252,255,0.85)'
      ctx.fillRect(cx + Math.cos(a) * size * 0.55, top + size * 0.4 + Math.sin(a) * size * 0.45, 1.5, 1.5)
    }
  }
  ctx.restore()
}
